import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, ChevronRight, Dumbbell, Camera, TrendingUp, MessageCircle } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import straivLogo from '@/assets/straiv-logo-tutorial-new.png';
import { Analytics } from '@/lib/analytics';

interface TutorialStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  targetElement?: string;
  position: 'center' | 'top' | 'bottom';
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    title: "Welcome to Straiv!",
    description: "Let's take a quick tour of what you can do here. Your AI fitness coach is here to guide you every step of the way.",
    icon: <img src={straivLogo} alt="Straiv" className="h-8 w-8 sm:h-12 sm:w-12" />,
    position: 'center'
  },
  {
    id: 2,
    title: "Log Your Workouts",
    description: "Track your gym sessions or running workouts with ease. Your AI coach learns from every workout you log to provide personalized guidance and track your progress.",
    icon: <Dumbbell className="h-8 w-8 sm:h-12 sm:w-12 text-primary" />,
    targetElement: '[data-tutorial="workouts"]',
    position: 'bottom'
  },
  {
    id: 3,
    title: "Nutrition & Sleep Tracking",
    description: "Use AI-powered photo scanning to instantly log your meals. Track your nutrition, water intake, and sleep quality. Your coach analyzes your data to optimize your nutrition goals.",
    icon: <Camera className="h-8 w-8 sm:h-12 sm:w-12 text-primary" />,
    targetElement: '[data-tutorial="nutrition"]',
    position: 'bottom'
  },
  {
    id: 4,
    title: "Monitor Your Progress",
    description: "View detailed charts and insights about your fitness journey. Track workout stats, body metrics, nutrition trends, and milestone achievements all in one place.",
    icon: <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-primary" />,
    targetElement: '[data-tutorial="progress"]',
    position: 'bottom'
  },
  {
    id: 5,
    title: "Chat with Your Coach",
    description: "Your AI coach is always available to answer questions, create custom workout plans, suggest recipes, and provide motivation. Just ask anything!",
    icon: <MessageCircle className="h-8 w-8 sm:h-12 sm:w-12 text-primary" />,
    targetElement: '[data-tutorial="chat"]',
    position: 'bottom'
  }
];

interface TutorialProps {
  onComplete: () => void;
}

export const Tutorial = ({ onComplete }: TutorialProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { updateProfile } = useProfile();

  const step = tutorialSteps[currentStep];

  // Show welcome toast when tutorial starts
  useEffect(() => {
    toast.success("Welcome to Straiv! Let's show you around.");
  }, []);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      Analytics.tutorialStepViewed(currentStep + 1, tutorialSteps[currentStep + 1].title);
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = async () => {
    setIsCompleting(true);
    try {
      Analytics.tutorialSkipped(currentStep);
      await updateProfile({ tutorial_completed: true });
      localStorage.setItem('tutorial_completed', 'true');
      localStorage.removeItem('force_tutorial');
      onComplete();
      navigate('/chat', { replace: true });
    } catch (error) {
      console.error('Error skipping tutorial:', error);
      toast.error('Failed to skip tutorial');
      setIsCompleting(false);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      Analytics.tutorialCompleted();
      await updateProfile({ tutorial_completed: true });
      localStorage.setItem('tutorial_completed', 'true');
      localStorage.removeItem('force_tutorial');
      toast.success('Tutorial completed! Welcome to Straiv!');
      // Wait a tick for state propagation
      setTimeout(() => {
        onComplete();
        navigate('/chat', { replace: true });
      }, 150);
    } catch (error) {
      console.error('Error completing tutorial:', error);
      toast.error('Failed to complete tutorial');
      setIsCompleting(false);
    }
  };

  // Auto-switch routes per step and bring target into view
  useEffect(() => {
    const routeMap: Record<number, string> = {
      1: '/dashboard',
      2: '/workouts',
      3: '/food-log',
      4: '/progress',
      5: '/dashboard', // keep bottom nav visible for chat highlight
    };
    const desired = routeMap[step.id];
    if (desired && location.pathname !== desired) {
      navigate(desired);
    }

    const selector = step.targetElement;
    if (selector) {
      const t = setTimeout(() => {
        const el = document.querySelector(selector);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [currentStep, step.id, step.targetElement, navigate, location.pathname]);

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Tutorial Card - compact on mobile */}
      <Card className="fixed bottom-[calc(72px+env(safe-area-inset-bottom,0px))] right-2 sm:bottom-20 sm:right-4 w-64 sm:w-80 border-2 border-primary/20 shadow-2xl animate-scale-in pointer-events-auto z-10">
        <CardContent className="p-3 sm:p-4">
          {/* Close/Skip Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6"
            onClick={handleSkip}
            disabled={isCompleting}
          >
            <X className="h-3 w-3" />
          </Button>

          {/* Icon & Step Counter */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-shrink-0">
              {step.icon}
            </div>
            <span className="text-xs text-muted-foreground">
              {step.id}/{tutorialSteps.length}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-base sm:text-lg font-bold mb-2 text-foreground pr-6">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-xs sm:text-sm text-muted-foreground mb-4 leading-relaxed">
            {step.description}
          </p>

          {/* Progress Dots */}
          <div className="flex gap-1.5 mb-3">
            {tutorialSteps.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? 'w-6 bg-primary'
                    : index < currentStep
                    ? 'w-1.5 bg-primary/50'
                    : 'w-1.5 bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              disabled={isCompleting}
              className="text-xs"
            >
              Skip
            </Button>
            <Button
              onClick={handleNext}
              disabled={isCompleting}
              size="sm"
              className="flex-1"
            >
              {currentStep === tutorialSteps.length - 1 ? (
                'Get Started'
              ) : (
                <>
                  Next
                  <ChevronRight className="ml-1 h-3 w-3" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
